<template lang="pug">
BlueSearch
	template(#default="{reloading}")
		.app(:class="classes")
			SearchQuery(v-if="config.showTrendingSearches")
				template(#default="{executeSearch, setQuery}")
					AppTrendingSearches(:executeSearch="executeSearch" :setQuery="setQuery")
			aside.app__rail(v-if="!config.hideRail")
				SearchFilters
					template(#group="{group, initiallyExpanded, setFilterValue, executeSearch, showZeroResultFilters}")
						AppFilters(
							:group="group"
							:initiallyExpanded="initiallyExpanded"
							:setFilterValue="setFilterValue"
							:executeSearch="executeSearch",
							:showZeroResultFilters="showZeroResultFilters")
			header.app__header(:class="{ 'app__header--allow-toggling': config.allowResultViewToggling }")
				.app-query__main
					SearchQuery
					AppViewToggle(v-if="config.allowResultViewToggling && resultView != 'calendar'")
				SearchQuery(v-if="!config.hideResultCount")
					template(#default="{results, currentResultsStart, currentResultsEnd}")
						AppResultCount(
							:results="results"
							:currentResultsStart="currentResultsStart"
							:currentResultsEnd="currentResultsEnd")
			aside.app__info(v-show="config.showTags")
				SearchTags
					template(#default="{tags, removeTag, clearAllTags}")
						AppTags(
							v-if="tags.length"
							:tags="tags"
							:removeTag="removeTag"
							:clearAllTags="clearAllTags"
							key="tags")
			section.app__body
				SearchResults
					template(#default="{results}")
						h2.app-results__heading-text(v-if="results.length") Results
						AppResults(:results="results")
			footer.app__footer
				SearchPagination
					template(v-if="config.preserveResults" #default="{currentPage, enableNext, navigate}")
						AppPagination(
							:currentPage="currentPage"
							:enableNext="enableNext"
							:navigate="navigate")
</template>

<script>
import { cloneDeep } from "lodash-es";
import { mapGetters, mapMutations } from "vuex";

import TransitionExpand from '../global/transitions/Expand.vue';

import AppFilters from './components/Filters.vue';
import AppTags from './components/Tags.vue';
import AppResultCount from './components/ResultCount.vue';
import AppResults from './components/Results.vue';
import AppPagination from './components/Pagination.vue';
import AppViewToggle from './components/ResultViewToggle';
import AppTrendingSearches from './components/TrendingSearches.vue';

export default {
	components: {
		TransitionExpand,
		AppFilters,
		AppResultCount,
		AppTags,
		AppResults,
		AppPagination,
		AppViewToggle,
		AppTrendingSearches
	},
	data() {
		return {
			currentQuery: "",
			dataLayerActive: false
		}
	},
	computed: {
		...mapGetters([
			"query",
			"results",
			"loaded",
			"siteName",
			"reloading",
			"resultView",
			"aboveMdBreakpoint",
			"filters",
		]),
		classes() {
			return {
				"reloading": this.reloading,
				"app--list-minimal": this.config.defaultResultView == "list-minimal",
				"app--calendar": this.resultView == "calendar" && this.aboveMdBreakpoint,
				"app--trending": this.config.showTrendingSearches && !!this.config.trendingSearchEndpoint
			}
		}
	},
	methods: {
		...mapMutations(["setStateValue", "extend"]),
		dataLayerSearchPush() {
			if (this.dataLayerActive) {
				let data = {
					'event': 'search_submit',
					'query': this.query
				}
				dataLayer.push(data);
				this.currentQuery = this.query
			}
		},
	},
	watch: {
		results(newResults, oldResults) {
			if (!!newResults.queryUsed?.searchTerm && newResults.queryUsed.searchTerm !== this.currentQuery) {
				this.dataLayerSearchPush();
			}
		},
		query(newQuery, oldQuery) {
			//we compare the current input of the search field against the query param in the url. If it's different, it's a new search term.
			let firstTimeQuery = false;
			if (!!newQuery && newQuery !== this.results.queryUsed?.searchTerm) {
				firstTimeQuery = true;
			}
			
			this.extend(extended => {
					let obj = cloneDeep(extended);
					obj.firstTimeQuery = firstTimeQuery;
					return obj;
				});
		}
	},
	created() {
		if (!!this.config.allowResultViewToggling && !!this.config.defaultResultView) {
			this.setStateValue(["resultView", this.config.defaultResultView]);
		} else if (!!this.config.defaultResultView) this.setStateValue(["resultView", this.config.defaultResultView]);
		else this.setStateValue(["resultView", "grid"]);
		
		if (window.dataLayer) this.dataLayerActive = true;

		if (!!document.querySelector(".page--RM")) this.setStateValue(["siteName", "RM"]);
		if (!!document.querySelector(".page--ORG, .page--TXC, .page--TSP, .page--BOB, .page--BUY")) this.setStateValue(["siteName", "ORG"]);
		if(this.config.siteNameOverride) this.setStateValue(["siteName", this.config.siteNameOverride]);
	},
	mounted() {
		window.addEventListener("resize", () => {
			this.setStateValue(["aboveMdBreakpoint", window.innerWidth > 768]);
		});
	},
}
</script>

<style lang="scss" scoped>
.app {
	display: flex;
	flex-direction: column;
	width: 100%;
	transition: 0.25s ease;

	&.reloading {
		filter: grayscale(50%) blur(4px);
		opacity: 0.5;
		pointer-events: none;
	}

	&--list-minimal {
		display: flex;
		grid-template-columns: unset;
		grid-template-rows: unset;
		.app__header {
			margin: 0;
		}
		.search-pagination {
			margin: 2rem 0 0;
		}
	}

	@media (min-width: 992px) {
		display: grid;
		grid-template-columns: minmax(200px, 25%) 1fr;
		grid-template-rows: auto auto 1fr auto;
		gap: 0 1.5rem;

		&--list-minimal {
			display: flex;
			grid-template-columns: unset;
			grid-template-rows: unset;
		}
	}

	&--calendar {
		@include mq(lg) {
			grid-template-columns: minmax(240px, 20%) 1fr;
		}
		.app-results {
			overflow: visible;
		}
		.app__footer {
			display: none;
		}
	}

	&--trending {
		grid-template-rows: auto auto 1fr auto auto;
		&:has(.app__info) {
			grid-template-rows: auto auto auto 1fr auto;
		}
		.app {
			&__rail {
				grid-area: 2/1/5/2;
			}
			&__header {
				grid-area: 2/2/2/3;
			}
			&__info {
				grid-area: 3/2/4/3;
			}
			&__body {
				grid-area: 4/2/5/3;
			}
			&__footer {
				grid-area: 5/2/6/3;
			}
		}
	}

	&__rail,
	&__header,
	&-query__main,
	&__info,
	&__body,
	&__footer {
		display: flex;
		flex-direction: column;
		gap: 1.5rem;
		max-width: 100%;
	}

	&__rail {
		grid-area: 1 / 1 / 5 / 2;
		order: 2;
	}

	&__header {
		grid-area: 1 / 2 / 2 / 3;
		order: 1;
		margin-bottom: 1.25rem;
		&.app__header--allow-toggling {
			
			.app-query__main {
				flex-direction: row;
				align-items: center;
			}
		}
	}
	
	&-query__main {
		align-items: center;
		@include mq(md) {
			flex-direction: row;
		}
	}

	&__info {
		grid-area: 2 / 2 / 3 / 3;
		order: 3;
	}

	&-results__heading-text {
		@include hideVisually;
	}
	&__body {
		grid-area: 3 / 2 / 4 / 3;
		order: 4;
		margin-top: 2rem;
	}

	&__footer {
		grid-area: 4 / 2 / 5 / 3;
		order: 5;
		align-items: center;
	}

	&::v-deep {
		.search-filters__toggle {
			@include buttonStyles;

			@media (min-width: 992px) {
				display: none;
			}
		}

		.search-query__results {
			display: none;
		}

		.search-query__form {
			overflow: hidden;
			display: flex;
			flex-direction: row;
			z-index: 1;
			.page--RM & {
				--focus-color: transparent;
				border: 1px solid $mediumGray;
				border-radius: 6px;
				&:focus-within {
					box-shadow: 0 0 0 1px var(--focus-color, $red);
				}
				@include mqdn(md) {
					flex-wrap: wrap;
				}
			}
			

			@include mqdn(md) {
				gap: 0;
				.page--ORG &,
				.page--TSP &,
				.page--TXC &,
				.page--BOB &,
				.page--BUY & {
					row-gap: 1.25rem;
					flex-wrap: wrap;
				}
			}
		}

		.search-query__wrapper {
			border: none;
			order: 1;
			input[type][name][class].search-query__input:is(#bluesearch *),
			input[type][name][class].search-query__input:is(body.using-mouse #bluesearch *) {
				padding-right: 0;
				box-shadow: 0 0 0 2px transparent !important;
				outline: none !important;
				&:focus {
					outline: none !important;
					box-shadow: 0 0 0 2px transparent !important;
				}
			}
			
			.page--ORG &,
			.page--TSP &,
			.page--TXC &,
			.page--BOB &,
			.page--BUY & {
				border-top: 1px solid $silverOrg;
				border-bottom: 1px solid $silverOrg;
				border-left: 1px solid $silverOrg;
				border-radius: 5px 0 0 5px;
			}
			.page--ORG & {
				&:focus-within {
					border-color: var(--focus-color, $blueOrg);
					box-shadow: 0 0 0 1px var(--focus-color, $blueOrg);
				}
			}
			.page--TXC & {
				&:focus-within {
					border-color: var(--focus-color, $compliantOrangeTxc);
					box-shadow: 0 0 0 1px var(--focus-color, $compliantOrangeTxc);
				}
			}
			.page--BOB & {
				&:focus-within {
					border-color: var(--focus-color, $accent900bob);
					box-shadow: 0 0 0 1px var(--focus-color, $accent900bob);
				}
			}
			.page--TSP & {
				&:focus-within {
					border-color: var(--focus-color, $blueTsp);
					box-shadow: 0 0 0 1px var(--focus-color, $blueTsp);
				}
			}

			@include mqdn(md) {
				flex: 0 0 calc(100% - 45px);
				max-width: calc(100% - 45px);
			}
		}

		.search-query__sort {
			cursor: pointer;
			select {
				cursor: pointer;
				position: relative;
				z-index: 2;
			}
			&::after {
				z-index: 1;
			}
		}

		.search-query__submit {
			padding: 0 0.75rem;
			background: transparent;
			color: $black;
			font-size: 0;
			order: 2;

			box-shadow: 0 0 0 2px transparent !important;
			outline: none !important;
			&:hover,
			&:focus {
				outline: none !important;
				box-shadow: 0 0 0 2px transparent !important;
				background: var(--button-hover-background, $blue);
				color: $white;
			}

			&::before {
				@include icon;
				font-size: 1.25rem;
				content: "\e962";
			}
			
			.page--ORG &,
			.page--TXC &,
			.page--TSP &,
			.page--BOB &,
			.page--BUY & {
				border-top: 1px solid $silverOrg;
				border-bottom: 1px solid $silverOrg;
				border-right: 1px solid $silverOrg;
				border-radius: 0 5px 5px 0;
				margin-right: 2rem;

				@include mqdn(md) {
					margin-right: 0;
				}
			}

			@include mqdn(md) {
				flex: 0 0 45px;
				max-width: 45px;
			}

		}
		
		button.search-query__reset {
			height: 100%;
			&:focus {
				color: $white;
				background: var(--button-hover-background, $blue);
			}
			
			.page--ORG &,
			.page--TXC &,
			.page--TSP &,
			.page--BOB &,
			.page--BUY & {
				span {
					display: none;
				}
				&::before {
					@include icon;
					content: "\e937";
					height: 1rem;
					width: 1rem;
					font-size: 0.5rem;
					background: #C6C6C7;
					color: $white;
					border-radius: 50%;
					display: flex;
					justify-content: center;
					align-items: center;
				}
			}
		}

		.search-query__sort {
			order: 3;
			margin: 0;
			max-width: 100%;

			&:focus-within:not(body.using-mouse *) {
				color: $white;
				background: $blue;
				.page--ORG & {
					background: $blueOrg;
				}
				.page--TXC & {
					background: $purpleTxc;
				}
				.page--BOB & {
					background: $primarybob;
				}
				.page--TSP & {
					background: $blueTsp;
				}
				select {
					color: $white;
					option {
						color: $black;
					}
				}
			}

			select {
				max-width: 100%;
			}

			.page--RM & {
				background: $lightGray;
				border: none;
				border-radius: 0;
			}
			.page--ORG &,
			.page--TXC &,
			.page--TSP &,
			.page--BOB &,
			.page--BUY & {
				background: $white;
				border: solid 1px $silverOrg;
				border-radius: 5px;
				select {
					color: $darkGrayOrg;
				}
			}

			@include mqdn(md) {
				flex: 1 1 100%;
				border-top: 1px solid $midGray;
			}

			@include mq(md) {
				border-left: 1px solid $midGray;

				select {
					padding-left: 1.25rem;
		
					.page--ORG &,
					.page--TXC &,
					.page--TSP &,
					.page--BOB &,
					.page--BUY & {
						padding-right: 3.75rem;
					}
				}
			}
		}
	}
}

</style>

<style lang="scss">
.page--ORG input.search-query__input:placeholder-shown + button.search-query__reset,
.page--TSP input.search-query__input:placeholder-shown + button.search-query__reset,
.page--TXC input.search-query__input:placeholder-shown + button.search-query__reset,
.page--BOB input.search-query__input:placeholder-shown + button.search-query__reset,
.page--BUY input.search-query__input:placeholder-shown + button.search-query__reset {
	display: none;
}
#bluesearch {
	.page--RM & {
		--focus-color: #{$red};
		--reset-color: #{$red};
		--link-color: #{$red};
		--link-hover-color: #{$blue};
		--loader-color: #{$red};
		--button-hover-background: #{$red};
		--font-family: #{$fontFamilyBaseRM};
	}
	.page--ORG & {
		--focus-color: #{$brightBlueAltOrg};
		--reset-color: #{$brightBlueAltOrg};
		--link-color: #{$linkColorORG};
		--link-hover-color: #{$linkColorHoverORG};
		--loader-color: #{$brightBlueAltOrg};
		--button-hover-background: #{$blueOrg};
		--font-family: #{$fontFamilyBaseORG};
		--pagination-number-active-background: #{$brightBlueAltOrg};
		--pagination-nav-hover-background: #{$brightBlueAltOrg};
		.app.app--list-minimal {
			.search-pagination {
				&__inner {
					gap: 0.25rem;
				}
				&__number,
				&__prev,
				&__next {
					font-size: 1.125rem;
					@include fluid(width, 1.875rem, 2.25rem);
					@include fluid(max-width, 1.875rem, 2.25rem);
					@include fluid(min-width, 1.875rem, 2.25rem);
					@include fluid(height, 1.875rem, 2.25rem);
					border-radius: 5px;
				}
			}
		}
		.search-pagination {
			&__prev,
			&__next,
			&__number {
				border-radius: 5px;
			}
		}
	}
	.page--TXC & {
		--focus-color: #{$compliantOrangeTxc};
		--reset-color: #{$primary700txc};
		--link-color: #{$linkColorTXC};
		--link-hover-color: #{$linkColorHoverTXC};
		--loader-color: #{$primary700txc};
		--button-hover-background: #{$purpleTxc};
		--font-family: #{$fontFamilyBaseORG};
		--pagination-number-active-background: #{$primary700txc};
		--pagination-nav-hover-background: #{$primary700txc};
		.app.app--list-minimal {
			.search-pagination {
				&__inner {
					gap: 0.25rem;
				}
				&__number,
				&__prev,
				&__next {
					font-size: 1.125rem;
					@include fluid(width, 1.875rem, 2.25rem);
					@include fluid(max-width, 1.875rem, 2.25rem);
					@include fluid(min-width, 1.875rem, 2.25rem);
					@include fluid(height, 1.875rem, 2.25rem);
					border-radius: 5px;
				}
			}
		}
		.search-pagination {
			&__prev,
			&__next,
			&__number {
				border-radius: 5px;
			}
		}
	}
	.page--BOB & {
		--focus-color: #{$accent900bob};
		--reset-color: #{$primary800bob};
		--link-color: #{$linkColorBOB};
		--link-hover-color: #{$linkColorHoverBOB};
		--loader-color: #{$primary800bob};
		--button-hover-background: #{$primarybob};
		--font-family: #{$fontFamilyBaseORG};
		--pagination-number-active-background: #{$primary800bob};
		--pagination-nav-hover-background: #{$primary800bob};
		.app.app--list-minimal {
			.search-pagination {
				&__inner {
					gap: 0.25rem;
				}
				&__number,
				&__prev,
				&__next {
					font-size: 1.125rem;
					@include fluid(width, 1.875rem, 2.25rem);
					@include fluid(max-width, 1.875rem, 2.25rem);
					@include fluid(min-width, 1.875rem, 2.25rem);
					@include fluid(height, 1.875rem, 2.25rem);
					border-radius: 5px;
				}
			}
		}
		.search-pagination {
			&__prev,
			&__next,
			&__number {
				border-radius: 5px;
			}
		}
	}
	.page--BUY & {
		--focus-color: #{$secondarybuy};
		--reset-color: #{$primary800buy};
		--link-color: #{$linkColorBUY};
		--link-hover-color: #{$linkColorHoverBUY};
		--loader-color: #{$primary600buy};
		--button-hover-background: #{$primarybuy};
		--font-family: #{$fontFamilyBaseORG};
		--pagination-number-active-background: #{$primary800buy};
		--pagination-nav-hover-background: #{$primary800buy};
		.app.app--list-minimal {
			.search-pagination {
				&__inner {
					gap: 0.25rem;
				}
				&__number,
				&__prev,
				&__next {
					font-size: 1.125rem;
					@include fluid(width, 1.875rem, 2.25rem);
					@include fluid(max-width, 1.875rem, 2.25rem);
					@include fluid(min-width, 1.875rem, 2.25rem);
					@include fluid(height, 1.875rem, 2.25rem);
					border-radius: 5px;
				}
			}
		}
		.search-pagination {
			&__prev,
			&__next,
			&__number {
				border-radius: 5px;
			}
		}
	}
	.page--TSP & {
		--focus-color: #{$primary600tsp};
		--reset-color: #{$primary600tsp};
		--link-color: #{$linkColorTSP};
		--link-hover-color: #{$linkColorHoverTSP};
		--loader-color: #{$primary600tsp};
		--button-hover-background: #{$blueTsp};
		--font-family: #{$fontFamilyBaseORG};
		--pagination-number-active-background: #{$primary600tsp};
		--pagination-nav-hover-background: #{$primary600tsp};
		.app.app--list-minimal {
			.search-pagination {
				&__inner {
					gap: 0.25rem;
				}
				&__number,
				&__prev,
				&__next {
					font-size: 1.125rem;
					@include fluid(width, 1.875rem, 2.25rem);
					@include fluid(max-width, 1.875rem, 2.25rem);
					@include fluid(min-width, 1.875rem, 2.25rem);
					@include fluid(height, 1.875rem, 2.25rem);
					border-radius: 5px;
				}
			}
		}
		.search-pagination {
			&__prev,
			&__next,
			&__number {
				border-radius: 5px;
			}
		}
	}
	.app {

		&--calendar.app {
			.search-query__sort{
				display: none;
			}

			.app__body {
				margin-top: 0.5rem;
			}

			.search-query__submit {
				margin-right: 0;
			}
		}
	}
}

body.using-mouse #bluesearch *:focus {
	outline: none;
	box-shadow: none;
}
</style>